import React, { useEffect } from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { BadgeSizes } from 'constants/badges';
import { BadgeMetadata, BadgeStyles } from 'types/badges';
import useWindowSize from 'hooks/useWindowSize';

import css from 'styles/components/common/badge.scss';

export interface BadgeProps {
  id: string;
  badgeName?: string;
  badgeStyles?: BadgeStyles[];
  category?: string;
  url?: string;
  badgeSize?: BadgeSizes;
  classNameWrapper?: string;
  badgeLinkClassName?: string;
  badgeIconClassName?: string;
  setLabel?: (label: string | HTMLElement) => void;
  productLink?: string;
  onBadgeCategoryClick?: () => void;
}

const Badge = ({
  id,
  badgeName,
  category,
  badgeStyles,
  url,
  badgeSize = BadgeSizes.MEDIUM,
  classNameWrapper,
  badgeLinkClassName,
  badgeIconClassName,
  setLabel,
  productLink,
  onBadgeCategoryClick
}: BadgeProps) => {
  const { styles, name } = useSelector(state => state.badges.metadata.find((badge: BadgeMetadata) => badge.id === id)) || {
    name: badgeName,
    styles: badgeStyles
  };

  const { width: windowWidth } = useWindowSize(500); // debounceWait=500

  useEffect(() => {
    if (setLabel) {
      const label = category ? name + ' ' + category : name;
      setLabel(label);
    }
  }, [category, setLabel, name]);

  if (!styles || !name) {
    return null;
  }

  const badgeStyling = [
    { [css.smallBadgeIconMobile]: badgeSize === BadgeSizes.SMALL },
    { [css.largeBadgeIconDesktopTablet]: badgeSize === BadgeSizes.LARGE }
  ];

  const categoryStyling = [{ [css.largeCategoryDesktopTablet]: badgeSize === BadgeSizes.LARGE }];

  const dynamicBackgroundStyle = {
    '--badgeBackgroundColor': styles[0].backgroundColor
  } as React.CSSProperties;

  const label = name.split(' ').map((word: string, i: number) => (
    <span
      key={word}
      className={css.badgeLabel}
      style={
        {
          '--badgeForegroundColor': styles[i]?.foregroundColor || styles[styles.length - 1].foregroundColor
        } as React.CSSProperties
      }
    >
      {i < name.split(' ').length - 1 ? word + ' ' : word}
    </span>
  ));

  const badgeCategory = category && (
    <p className={cn(css.category, categoryStyling)} title={category}>
      {category}
    </p>
  );

  const createBadgeIcon = (customStyle: string | undefined) => (
    <div style={dynamicBackgroundStyle} className={cn(css.badgeIcon, badgeStyling, customStyle)}>
      {label}
    </div>
  );

  const badgeCategoryLink = windowWidth && windowWidth <= 650 ? productLink : url;

  return (
    <div className={cn(css.badgeWrapper, classNameWrapper)}>
      {url && badgeCategory ? (
        <a href={badgeCategoryLink || url} className={css.badgeLink} onClick={onBadgeCategoryClick}>
          {createBadgeIcon(badgeLinkClassName)}
          {badgeCategory}
        </a>
      ) : (
        <a href={productLink}>{createBadgeIcon(badgeIconClassName)}</a>
      )}
    </div>
  );
};

export default Badge;
